/**
 * Глобальный контекст интерактивных (embed) сессий оценки Hermes.
 *
 * Позволяет запускать диалоговую оценку записи из любого места приложения
 * (строка грида, bulk-выделение, вкладка «Оценка» карточки). Сессии живут на
 * уровне оболочки, поэтому переживают уход с карточки и смену страницы. Сам
 * iframe и postMessage-мост рендерит HermesSessionDock.
 *
 * docs/modules/hermes-embed.md.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import type { HermesEstimationMode, HermesEmbedPreset } from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import { createEmbedSession } from './api.ts';

export type HermesSessionStatus =
  | 'opening' // получаем embed-токен
  | 'ready' // iframe загружен, ждём hermes:ready/context
  | 'working' // Hermes считает
  | 'needs_input' // Hermes ждёт ответа пользователя
  | 'done' // hermes:done получен, результат финализирован
  | 'error' // ошибка сессии/Hermes
  | 'expired'; // embed-токен протух (TTL)

/** Статус загрузки списка пресетов (discovery GET .../hermes-presets). */
export type HermesPresetsStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface HermesSession {
  /** Локальный id сессии (не task_id). */
  id: string;
  spaceId: string;
  itemId: string;
  itemKey?: string;
  title: string;
  /** Формат оценки (auto/high_level/detailed). */
  mode: HermesEstimationMode;
  /** Выбранный пресет (id из discovery) — undefined, пока не загружены/нет. */
  preset?: string;
  /** Доступные пресеты (GET .../hermes-presets); пустой массив — селектор скрыт. */
  presets?: HermesEmbedPreset[];
  /** Статус загрузки списка пресетов. */
  presetsStatus?: HermesPresetsStatus;
  status: HermesSessionStatus;
  /** Заполняется после ответа embed-session. */
  embedToken?: string;
  hermesOrigin?: string;
  embedUrl?: string;
  taskDescription?: string;
  /** Когда протухает embed-токен (мс epoch) — для детекта 'expired'. */
  expiresAt?: number;
  /** Приходит на hermes:done. */
  taskId?: string;
  error?: string;
}

export interface LaunchSession {
  spaceId: string;
  itemId: string;
  itemKey?: string;
  title: string;
  mode?: HermesEstimationMode;
}

/**
 * Ширина выезжающей панели — компактная и развёрнутая. В панели живёт диалог
 * Гермеса со своей вёрсткой: на 420px он ужимался в узкую колонку, поэтому
 * компактный режим шире, а развёрнутый занимает почти весь экран.
 */
export const HERMES_DRAWER_WIDTH_COMPACT = 560;
export const HERMES_DRAWER_WIDTH_WIDE = 1080;

interface HermesSessionsContextValue {
  sessions: HermesSession[];
  open: boolean;
  activeId: string | null;
  /** Развёрнута ли панель на широкий режим. */
  expanded: boolean;
  /** Текущая ширина панели (px) — для сдвига контента в AppLayout. */
  drawerWidth: number;
  toggleExpanded: () => void;
  /** Запустить новую сессию (или сфокусировать существующую для этой записи). */
  openSession: (launch: LaunchSession) => void;
  showDrawer: () => void;
  closeDrawer: () => void;
  setActive: (id: string) => void;
  closeSession: (id: string) => void;
  updateSession: (id: string, patch: Partial<HermesSession>) => void;
  /** Переоткрыть истёкшую сессию (новый embed-токен). */
  reopenSession: (id: string) => void;
}

const HermesSessionsCtx = createContext<HermesSessionsContextValue | null>(null);

function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `hs_${Date.now()}_${Math.floor(Math.random() * 1e9)}`;
}

export function HermesSessionsProvider({ children }: PropsWithChildren): JSX.Element {
  const [sessions, setSessions] = useState<HermesSession[]>([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  // Чтобы не плодить сессии на одну запись, держим карту itemId → sessionId.
  const byItem = useRef<Map<string, string>>(new Map());

  const updateSession = useCallback((id: string, patch: Partial<HermesSession>) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }, []);

  const requestToken = useCallback(
    async (id: string, spaceId: string, itemId: string, mode?: HermesEstimationMode) => {
      try {
        const res = await createEmbedSession(spaceId, itemId, mode);
        updateSession(id, {
          embedToken: res.embedToken,
          hermesOrigin: res.hermesOrigin,
          embedUrl: res.embedUrl,
          taskDescription: res.taskDescription,
          mode: res.mode,
          expiresAt: res.expiresIn ? Date.now() + res.expiresIn * 1000 : undefined,
          status: 'ready',
          error: undefined,
        });
      } catch (err) {
        updateSession(id, {
          status: 'error',
          error: err instanceof ApiError ? err.message : 'Не удалось открыть сессию оценки',
        });
      }
    },
    [updateSession],
  );

  const openSession = useCallback(
    (launch: LaunchSession) => {
      const existing = byItem.current.get(launch.itemId);
      if (existing) {
        setActiveId(existing);
        setOpen(true);
        return;
      }
      const id = newId();
      byItem.current.set(launch.itemId, id);
      const mode = launch.mode ?? 'auto';
      setSessions((prev) => [
        ...prev,
        {
          id,
          spaceId: launch.spaceId,
          itemId: launch.itemId,
          itemKey: launch.itemKey,
          title: launch.title,
          mode,
          presetsStatus: 'idle',
          status: 'opening',
        },
      ]);
      setActiveId(id);
      setOpen(true);
      void requestToken(id, launch.spaceId, launch.itemId, launch.mode);
    },
    [requestToken],
  );

  const reopenSession = useCallback(
    (id: string) => {
      const s = sessions.find((x) => x.id === id);
      if (!s) return;
      updateSession(id, { status: 'opening', embedToken: undefined, error: undefined, taskId: undefined });
      void requestToken(id, s.spaceId, s.itemId, s.mode);
    },
    [sessions, requestToken, updateSession],
  );

  const closeSession = useCallback((id: string) => {
    setSessions((prev) => {
      const next = prev.filter((s) => s.id !== id);
      const removed = prev.find((s) => s.id === id);
      if (removed) byItem.current.delete(removed.itemId);
      return next;
    });
    setActiveId((cur) => {
      if (cur !== id) return cur;
      // Активной станет первая из оставшихся, либо null.
      return null;
    });
  }, []);

  const showDrawer = useCallback(() => setOpen(true), []);
  const closeDrawer = useCallback(() => setOpen(false), []);
  const toggleExpanded = useCallback(() => setExpanded((v) => !v), []);
  const setActive = useCallback((id: string) => {
    setActiveId(id);
    setOpen(true);
  }, []);

  // На узком экране развёрнутая панель шире окна — оставляем полоску, за
  // которую видно таблицу и по которой панель закрывают кликом мимо.
  const maxDrawerWidth = typeof window === 'undefined' ? Infinity : Math.max(320, window.innerWidth - 80);
  const drawerWidth = Math.min(expanded ? HERMES_DRAWER_WIDTH_WIDE : HERMES_DRAWER_WIDTH_COMPACT, maxDrawerWidth);

  const value = useMemo<HermesSessionsContextValue>(
    () => ({
      sessions,
      open,
      activeId,
      expanded,
      drawerWidth,
      toggleExpanded,
      openSession,
      showDrawer,
      closeDrawer,
      setActive,
      closeSession,
      updateSession,
      reopenSession,
    }),
    [sessions, open, activeId, expanded, drawerWidth, toggleExpanded, openSession, showDrawer, closeDrawer, setActive, closeSession, updateSession, reopenSession],
  );

  return <HermesSessionsCtx.Provider value={value}>{children}</HermesSessionsCtx.Provider>;
}

export function useHermesSessions(): HermesSessionsContextValue {
  const ctx = useContext(HermesSessionsCtx);
  if (!ctx) throw new Error('useHermesSessions must be used within HermesSessionsProvider');
  return ctx;
}
